import { COLORS } from '../../../IndividualPlan/tokens';
import type { ChartPoint } from './widaTrajectoryData';
import { formatSchoolYearShort } from './YearAxisTick';

type WidaTrajectoryProficiencyChartProps = {
  points: ChartPoint[];
  threshold?: number | null;
  thresholdLabel?: string;
  height?: number;
  width?: number;
  fontSize?: number;
  variant?: 'default' | 'perDomain';
  accentColor?: string;
};

const PL_MIN = 1;
const PL_MAX = 6;
const PL_TICKS = [1, 2, 3, 4, 5, 6];

export function WidaTrajectoryProficiencyChart({
  points,
  threshold,
  thresholdLabel,
  height = 260,
  width = 360,
  fontSize = 16,
  variant = 'default',
  accentColor = COLORS.blue,
}: WidaTrajectoryProficiencyChartProps) {
  const perDomain = variant === 'perDomain';
  const left = perDomain ? 34 : 56;
  const right = perDomain ? 34 : 16;
  const top = perDomain ? 34 : 24;
  const bottom = perDomain ? 34 : 40;
  const innerW = width - left - right;
  const innerH = height - top - bottom;

  const slotCount = points.length;
  const x = (index: number) => {
    if (slotCount <= 1) return left + innerW / 2;
    return left + (index / (slotCount - 1)) * innerW;
  };
  const y = (value: number) =>
    top + (1 - (value - PL_MIN) / (PL_MAX - PL_MIN)) * innerH;

  const polyline = points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ');

  const hasThreshold = !perDomain && typeof threshold === 'number' && Number.isFinite(threshold);
  const thresholdText = thresholdLabel ?? `exit target · PL ${threshold}`;
  const lineColor = perDomain ? accentColor : COLORS.navy;
  const dotRadius = perDomain ? 7 : 6;
  const valueGap = dotRadius + fontSize * 0.6;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      style={{ height: 'auto', maxHeight: height, display: 'block' }}
      role="img"
      aria-label="WIDA proficiency-level trajectory"
    >
      {!perDomain &&
        PL_TICKS.map((t) => (
          <g key={`tick-${t}`}>
            <line x1={left} y1={y(t)} x2={width - right} y2={y(t)} stroke={COLORS.hairlineSoft} />
            <text x={left - 8} y={y(t) + 5} textAnchor="end" fill={COLORS.muted} fontSize={fontSize} fontWeight={500}>
              {t.toFixed(1)}
            </text>
          </g>
        ))}

      {perDomain && (
        <line x1={0} y1={top + innerH} x2={width} y2={top + innerH} stroke={COLORS.hairline} />
      )}

      {hasThreshold && (
        <g>
          <line
            x1={left}
            y1={y(threshold)}
            x2={width - right}
            y2={y(threshold)}
            stroke={COLORS.navy}
            strokeWidth={1.5}
            strokeDasharray="4 3"
          />
          <text x={width - right} y={y(threshold) - 6} textAnchor="end" fill={COLORS.navy} fontSize={fontSize} fontWeight={600}>
            {thresholdText}
          </text>
        </g>
      )}

      {points.length > 1 && (
        <polyline
          points={polyline}
          fill="none"
          stroke={lineColor}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {points.map((p, i) => (
        <circle
          key={`pt-${p.schoolYear}`}
          cx={x(i)}
          cy={y(p.value)}
          r={dotRadius}
          fill="#FFFFFF"
          stroke={lineColor}
          strokeWidth={2}
        />
      ))}

      {perDomain &&
        points.map((p, i) => (
          <text
            key={`value-${p.schoolYear}`}
            x={x(i)}
            y={y(p.value) - valueGap}
            textAnchor="middle"
            fill={COLORS.muted}
            fontSize={fontSize}
            fontWeight={500}
          >
            {p.value.toFixed(1)}
          </text>
        ))}

      {points.map((p, i) => (
        <text
          key={`xlabel-${p.schoolYear}`}
          x={x(i)}
          y={height - 12}
          textAnchor="middle"
          fill={COLORS.muted}
          fontSize={fontSize}
          fontWeight={600}
        >
          {formatSchoolYearShort(p.schoolYear)}
        </text>
      ))}
    </svg>
  );
}
